import React, { useCallback } from 'react'
import { useRouteMatch, useParams } from 'react-router-dom'
import parseError from 'helpers/errorHelpers'
import {
  usePackage,
  useReadPackage,
  useParseOPF,
} from 'api/Package/Packages/queries'
import { useLocation } from 'react-router'
import { useAttempt, useAttemptsV2 } from 'api/Package/Attempts/queries'
import { userRoleSelector } from 'store/auth/selectors'
import { useUserMissions } from 'api/Gamification/UserMissions/queries'
import { useSchoolAssignment } from 'api/Package/SchoolAssignments/queries'
import { useMission } from 'api/Gamification/Missions/queries'
import useNotification from 'hooks/useNotification'
import PackageService from 'api/Package'
import { useSelector } from 'react-redux'
import { generateFilter } from 'helpers/api'
import { groupBy, uniqBy } from 'lodash'
import { useCurrentUser } from 'api/User/Users/queries'
import { useContext } from 'react'
import { useMemo } from 'react'
import { useGetCookiesScorm } from 'api/Package/Packages/queries'
import { useParseScorm } from 'api/Package/Packages/queries'
import useStorage from 'hooks/useStorage'
import { usePackageActivities } from 'api/Package/Activitites/queries'

const IS_STUDENT = ['Student', 'Member']

const CAN_SHOW_ATTEMPTS = [
  'School Master',
  'School Manager',
  'Individual Teacher',
  'Admin',
  'Teacher',
]

export const useReaderContextProvider = () => {
  const currentUser = useCurrentUser()

  const IS_AUTHENTICATED = useMemo(
    () => currentUser.isSuccess,
    [currentUser.isSuccess]
  )

  const { addNotification } = useNotification()

  let matchAssignmentWithPath = useRouteMatch({
    path: '/reader/assignment/:id/:packageId/:href(.*)',
    strict: true,
    sensitive: true,
  })

  let matchAssignment = useRouteMatch({
    path: '/reader/assignment/:id',
    strict: true,
    sensitive: true,
  })

  // --------------

  let matchMission = useRouteMatch({
    path: '/reader/mission/:id',
    strict: true,
    sensitive: true,
  })
  // --------------

  let matchAttempt = useRouteMatch({
    path: '/reader/attempt/:id',
    strict: true,
    sensitive: true,
  })
  // --------------
  let matchPreviewMission = useRouteMatch({
    path: '/reader/previewmission/:id',
    strict: true,
    sensitive: true,
  })
  // --------------
  let matchRead = useRouteMatch({
    path: '/reader/:readMode/:packageId',
    strict: true,
    sensitive: true,
  })
  let matchReadWithId = useRouteMatch({
    path: '/reader/:packageId/:readMode/:id',
    strict: true,
    sensitive: true,
  })

  let matchReadWithPath = useRouteMatch({
    path: '/reader/:readMode/:packageId/:href(.*)',
    strict: true,
    sensitive: true,
  })

  const location = useLocation()

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const attemptId = useMemo(
    () => (!matchAttempt ? params.get('attempt') : matchAttempt.params.id),
    [matchAttempt, params]
  )

  const activityId = useMemo(() => params.get('activityId'), [params])

  const packageId = useMemo(
    () =>
      matchAssignmentWithPath?.params?.packageId
        ? matchAssignmentWithPath?.params?.packageId
        : matchReadWithPath?.params?.packageId
        ? matchReadWithPath?.params?.packageId
        : params.get('packageId'),
    [
      matchAssignmentWithPath?.params?.packageId,
      matchReadWithPath?.params?.packageId,
      params,
    ]
  )

  const path = useMemo(
    () =>
      matchAssignmentWithPath?.params?.href
        ? matchAssignmentWithPath?.params?.href
        : matchReadWithPath?.params?.href
        ? matchReadWithPath?.params?.href
        : params.get('path'),
    [
      matchAssignmentWithPath?.params?.href,
      matchReadWithPath?.params?.href,
      params,
    ]
  )

  const [pages, setPages] = React.useState([])
  const [currentPage, setCurrentPage] = React.useState(0)

  const [availableActivities, setAvailableActivities] = React.useState([])
  const [currentActivity, setCurrentActivity] = React.useState()
  const [currentIndex, setCurrentIndex] = React.useState(0)

  const { readMode } = useParams()

  const IS_ASSIGNMENT = useMemo(() => {
    return !!matchAssignment?.isExact && readMode === 'assignment'
  }, [matchAssignment?.isExact, readMode])

  const IS_ASSIGNMENT_PREVIEW = useMemo(() => {
    return readMode === 'assignmentpreview'
  }, [readMode])

  const IS_ASSIGNMENT_DIGIIEBOOK = useMemo(() => {
    return readMode === 'assignment' && !!matchAssignmentWithPath?.params?.href
  }, [readMode, matchAssignmentWithPath?.params?.href])

  const IS_MISSION = useMemo(() => {
    return !!matchMission?.isExact && readMode === 'mission'
  }, [matchMission?.isExact, readMode])

  const IS_ATTEMPT = useMemo(() => {
    return !!matchAttempt?.isExact && readMode === 'attempt'
  }, [matchAttempt?.isExact, readMode])

  const IS_PREVIEW_MISSION = useMemo(() => {
    return !!matchRead?.isExact && readMode === 'previewmission'
  }, [matchRead?.isExact, readMode])

  // --------------

  const assignment = useSchoolAssignment(matchAssignment?.params?.id, {
    queryFilters: { includeModels: ['Packages'] },
    queryOptions: {
      enabled: !!IS_ASSIGNMENT && IS_AUTHENTICATED,
      staleTime: 60 * 60 * 1000,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error getting Assignment'),
        })
      },
    },
  })

  const mission = useUserMissions(null, {
    queryFilters: { filters: [{ by: 'id', value: matchMission?.params?.id }] },
    queryOptions: {
      enabled: !!IS_MISSION && IS_AUTHENTICATED,
      staleTime: 60 * 60 * 1000,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error getting Mission'),
        })
      },
    },
  })

  // ------------------------
  const previewMission = useMission(matchPreviewMission?.params?.id, {
    queryOptions: {
      enabled: !!IS_PREVIEW_MISSION && IS_AUTHENTICATED,
      staleTime: 60 * 60 * 1000,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error getting Mission'),
        })
      },
    },
  })
  // ------------------------

  const IS_READ = useMemo(() => {
    return (
      !!matchRead?.isExact &&
      readMode === 'read' &&
      !matchReadWithId &&
      IS_AUTHENTICATED
    )
  }, [matchRead?.isExact, readMode, matchReadWithId, IS_AUTHENTICATED])

  const IS_READ_WITH_ID = useMemo(() => {
    return !!matchReadWithId?.isExact && IS_AUTHENTICATED
  }, [matchReadWithId?.isExact, IS_AUTHENTICATED])

  const ENABLE_GET_PACKAGE = useMemo(
    () =>
      IS_ASSIGNMENT
        ? assignment.isSuccess
        : IS_MISSION
        ? mission.isSuccess
        : IS_ATTEMPT
        ? true
        : IS_PREVIEW_MISSION
        ? previewMission.isSuccess
        : IS_ASSIGNMENT_PREVIEW
        ? true
        : IS_ASSIGNMENT_DIGIIEBOOK
        ? true
        : IS_READ || IS_READ_WITH_ID,
    [
      IS_ASSIGNMENT,
      IS_MISSION,
      IS_ATTEMPT,
      IS_PREVIEW_MISSION,
      IS_ASSIGNMENT_PREVIEW,
      IS_ASSIGNMENT_DIGIIEBOOK,
      IS_READ,
      IS_READ_WITH_ID,
      assignment.isSuccess,
      mission.isSuccess,
      previewMission.isSuccess,
    ]
  )

  const packageToGet = useMemo(
    () =>
      IS_ASSIGNMENT
        ? assignment.data?.Package.id
        : IS_MISSION
        ? mission.data?.items[0]?.Mission.package_id
        : IS_ATTEMPT
        ? packageId
        : IS_PREVIEW_MISSION
        ? previewMission.data?.package_id
        : IS_READ || IS_READ_WITH_ID
        ? matchRead?.params?.packageId
        : IS_ASSIGNMENT_PREVIEW
        ? matchReadWithPath?.params?.packageId
        : IS_ASSIGNMENT_DIGIIEBOOK
        ? matchAssignmentWithPath?.params?.packageId
        : '',
    [
      IS_ASSIGNMENT,
      IS_MISSION,
      IS_ATTEMPT,
      IS_PREVIEW_MISSION,
      IS_READ,
      IS_READ_WITH_ID,
      IS_ASSIGNMENT_PREVIEW,
      IS_ASSIGNMENT_DIGIIEBOOK,
      assignment.data?.Package.id,
      mission.data?.items,
      packageId,
      previewMission.data?.package_id,
      matchRead?.params?.packageId,
      matchReadWithPath?.params?.packageId,
      matchAssignmentWithPath?.params?.packageId,
    ]
  )

  const pkg = usePackage(packageToGet, {
    queryOptions: {
      enabled: ENABLE_GET_PACKAGE,
      retry: false,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, `Error finding Package`),
        })
      },
    },
  })

  // const IS_HTML5 = false
  const IS_HTML5 = useMemo(() => {
    return pkg.data?.Format.title === 'html5'
  }, [pkg.data?.Format.title])

  const IS_IWB = useMemo(() => {
    return pkg.data?.Format?.title === 'iwb'
  }, [pkg.data?.Format.title])
  const IS_DIGIIWB = useMemo(() => {
    return pkg.data?.Format?.title === 'digiiwb'
  }, [pkg.data?.Format.title])
  const IS_EPUB = useMemo(() => {
    return pkg.data?.Format.title === 'epub3'
  }, [pkg.data?.Format?.title])
  const IS_DIWB = useMemo(() => {
    return pkg.data?.Format?.title === 'diwb'
  }, [pkg.data?.Format.title])
  const IS_DIGIPUB = useMemo(() => {
    return pkg.data?.Format?.title === 'digipub'
  }, [pkg.data?.Format.title])
  const IS_SCORM = useMemo(() => {
    return pkg.data?.Format?.title === 'scorm'
  }, [pkg.data?.Format.title])
  const IS_DIGIIEBOOK = useMemo(() => {
    return pkg.data?.Format?.title === 'digiiebook'
  }, [pkg.data?.Format.title])

  const readPackage = useReadPackage(packageToGet, {
    queryOptions: {
      enabled: pkg.isSuccess,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error reading Package'),
        })
      },
    },
  })

  const getSignedCookies = useGetCookiesScorm({
    queryOptions: {
      enabled: IS_SCORM && pkg.isSuccess,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error getting signed cookies'),
        })
      },
    },
  })

  let userRole = useSelector(userRoleSelector)

  const attempts = useAttempt(attemptId, {
    queryOptions: {
      enabled:
        (CAN_SHOW_ATTEMPTS.includes(userRole) &&
          !!attemptId &&
          (IS_EPUB || IS_DIWB)) ||
        IS_ATTEMPT,
      staleTime: 1000 * 240,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error getting Attempt'),
        })
      },
    },
  })

  // ------------------------

  const digiiebooksActivities = usePackageActivities(packageToGet, null, {
    queryOptions: {
      enabled: IS_DIGIIEBOOK && !!packageToGet,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, 'Error parsing Activities'),
        })
      },
    },
  })

  const [currentActivityWithID, setCurrentActivityWithId] = React.useState()

  React.useEffect(() => {
    if (IS_DIGIIEBOOK && digiiebooksActivities.data?.items && currentActivity) {
      const activityWithId = digiiebooksActivities.data.items.find(
        (act) =>
          act.path === currentActivity.href || act.path === currentActivity.path
      )
      setCurrentActivityWithId(activityWithId)
    }
  }, [IS_DIGIIEBOOK, digiiebooksActivities.data?.items, currentActivity])

  const currentSubSchool = useSelector((state) => state.auth.currentSubSchool)
  const currentSchoolID = useMemo(() => {
    const SCHOOL_TYPE_2 = currentSubSchool?.type_id === 2
    return SCHOOL_TYPE_2 ? currentSubSchool?.user_id : currentSubSchool?.id
  }, [
    currentSubSchool?.type_id,
    currentSubSchool?.user_id,
    currentSubSchool?.id,
  ])
  const currentSchoolYear = useSelector((state) => state.auth.currentSchoolYear)

  const lastAttempt = useAttemptsV2(
    currentSchoolID,
    currentSchoolYear?.id,
    null,
    {
      queryFilters: {
        filters: [
          { by: 'activity_id', value: currentActivity?.activityId },
          { by: 'sort_by', value: 'created.desc' },
        ],
      },
      queryOptions: {
        enabled:
          IS_STUDENT.includes(userRole) &&
          !!currentActivity?.activityId &&
          (IS_EPUB || IS_DIWB) &&
          !IS_MISSION,
        onError: (error) => {
          addNotification({
            type: 'error',
            message: parseError(error, 'Error getting Attempt'),
          })
        },
      },
    },
    'single'
  )

  const dateFilter = useMemo(
    () =>
      mission.data?.items?.[0]?.created
        ? [{ by: 'created[gte]', value: mission.data?.items?.[0]?.created }]
        : [],
    [mission.data?.items]
  )
  const completedUserMissionActivities =
    PackageService.Attempts.hooks.useAttemptsActivitiesNEWV2(
      {
        subSchoolId: currentSchoolID,
        schoolYearId: currentSchoolYear?.id,
        filters: [
          ...dateFilter,
          { by: 'user_id', value: currentUser.data?.id },
          ...generateFilter(
            [],
            mission?.data?.items?.[0]?.Mission.params,
            'activity_id'
          ),
        ],
      },
      {
        enabled: IS_MISSION && mission.isSuccess,
      }
    )

  // const parseOPF = useParseOPF(readPackage.data?.url, {
  const parseOPF = useParseOPF(readPackage.data?.url, pkg.data?.version, {
    queryOptions: {
      enabled:
        readPackage.isSuccess &&
        (IS_EPUB || IS_DIWB) &&
        (IS_MISSION ? completedUserMissionActivities.isSuccess : true),
      onSuccess: (data) => {
        if (IS_PREVIEW_MISSION) {
          let missionActivities = data[1].filter((activity) =>
            previewMission.data?.params.includes(parseInt(activity.activityId))
          )
          setAvailableActivities(missionActivities)
          setCurrentActivity(missionActivities[0])
          setCurrentIndex(0)
        } else if (IS_READ) {
          setAvailableActivities(data[1])
          setPages(data[3])
          setCurrentPage(data[3][0]?.href)
          setCurrentActivity(data[1][0])
          setCurrentIndex(0)
        } else if (IS_READ_WITH_ID) {
          setAvailableActivities(data[1])
          setPages(data[3])
          setCurrentPage(data[3][0]?.href)
          let activityIndex = data[1].findIndex(
            (activity) => activity.id === matchReadWithId?.params?.id
          )
          if (activityIndex !== -1) {
            setCurrentActivity(data[1][activityIndex])
            setCurrentIndex(activityIndex)
          } else {
            setCurrentActivity(data[1][0])
            setCurrentIndex(0)
          }
        } else if (IS_ASSIGNMENT) {
          let assignmentActivity = data[1].find(
            (activity) =>
              parseInt(activity.activityId) === assignment.data?.activity_id
          )
          setAvailableActivities([{ ...assignmentActivity }])
          setCurrentActivity(assignmentActivity)
          setCurrentIndex(0)
        } else if (IS_MISSION) {
          let uniqCompleted = uniqBy(
            completedUserMissionActivities.data?.items ?? [],
            'id'
          )
          let uniqCompletedIDs = uniqCompleted.map((item) => item?.id)
          let missionActivities = data[1].filter(
            (activity) =>
              mission.data?.items?.[0].Mission?.params.includes(
                parseInt(activity.activityId)
              ) && !uniqCompletedIDs.includes(parseInt(activity.activityId))
          )

          setAvailableActivities(missionActivities)
          setCurrentActivity(missionActivities[0])
          setCurrentIndex(0)
        } else if (IS_ATTEMPT) {
          let assignmentActivity = data[1].find(
            (activity) => activity.activityId === activityId
          )
          setAvailableActivities([{ ...assignmentActivity }])
          setCurrentActivity(assignmentActivity)
          setCurrentIndex(0)
        }
      },
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, `Error parsing Package`),
        })
      },
    },
  })

  const parseScorm = useParseScorm(readPackage.data?.url, {
    queryOptions: {
      enabled: IS_SCORM && readPackage.isSuccess,
      onSuccess: (data) => {
        setPages(data?.pages ?? [])
        setAvailableActivities(data?.pages ?? [])
        setCurrentIndex(0)
        setCurrentActivity(data?.pages?.[0] ?? null)
        setTimeout(() => {
          iRef.current?.contentWindow?.postMessage(
            JSON.stringify({
              action: 'change page',
              src: readPackage.data?.url + data?.pages?.[0] ?? '',
            }),
            '*'
          )
        }, 2000)
        // setCurrentActivity(data?.pages?.[0] ?? null)
        // setAvailableActivities(data[1])
        // setCurrentActivity(data[1][0])
        // setPages(data[3])
        // setCurrentPage(data[3][0]?.href)
      },
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(error, `Error parsing SCORM Package`),
        })
      },
    },
  })

  const IS_READING =
    readPackage.isLoading || parseOPF.isLoading || parseScorm.isLoading

  const SHOW_CONTENT =
    IS_EPUB || IS_DIWB
      ? pkg.isSuccess && readPackage.isSuccess && parseOPF.isSuccess
      : IS_SCORM
      ? // ? pkg.isSuccess && readPackage.isSuccess && parseScorm.isSuccess
        pkg.isSuccess && readPackage.isSuccess // TODO: Fix this logic
      : pkg.isSuccess && readPackage.isSuccess

  let [currentTab, setCurrentTab] = React.useState(0)

  const tabs = [{ text: 'contents' }, { text: 'pages' }]

  const isTabActivities = currentTab === 0
  const isTabPages = currentTab === 1

  const baseURL = useMemo(() => {
    if (IS_SCORM) {
      // return getSignedCookies.data?.scorm || ''
      return getSignedCookies.data?.scorm || ''
    }
    if (!readPackage.data?.url) return ''
    return readPackage.data.url
  }, [IS_SCORM, readPackage, getSignedCookies.data])

  const epub3URL = useMemo(() => {
    if (!currentActivity || (pages.length && !currentPage)) return ``
    return isTabActivities
      ? `${baseURL}OPS/${currentActivity.href}`
      : `${baseURL}OPS/${currentPage}`
  }, [baseURL, currentActivity, currentPage, isTabActivities, pages])

  const html5URL = `${baseURL}index.html`

  const digiiebookURL =
    IS_ATTEMPT ||
    IS_ASSIGNMENT ||
    IS_ASSIGNMENT_PREVIEW ||
    IS_ASSIGNMENT_DIGIIEBOOK
      ? `${baseURL}${path}`
      : `${baseURL}index.html`

  const iwbURL = `${baseURL}index.html`

  const digiiwbURL = `${baseURL}index.html`

  const digiURL = `${baseURL}index.html`

  const scormURL = useMemo(() => {
    // return IS_SCORM ? '/test/shared/launchpage.html' : ''
    return IS_SCORM ? baseURL : ''
  }, [IS_SCORM, baseURL])
  const iRef = React.useRef()

  const [latestAttempts] = useStorage('latestAttempts', [], 'session')

  const studentAnswers = useMemo(() => {
    let currentActivityAttempts
    if (latestAttempts?.length) {
      currentActivityAttempts = latestAttempts
        .filter(
          (attempt) => attempt.activity_id === currentActivity?.activityId
        )
        .sort((a, b) => b.created - a.created)
    }

    if (currentActivityAttempts?.length) {
      return currentActivityAttempts[0].answers
    }
    if (attempts?.data?.answers?.length) {
      return attempts?.data?.answers
    } else if (lastAttempt?.data?.items?.length) {
      return lastAttempt?.data?.items[0]?.answers
    }
  }, [attempts, lastAttempt, latestAttempts, currentActivity])

  let groupedActivities = React.useMemo(
    () => groupBy(availableActivities, 'moduleName'),
    [availableActivities]
  )

  const [open, setOpen] = React.useState(false)

  const handleToggleDrawer = () => {
    setOpen((open) => !open)
  }

  // DIGIPUB
  const [toc, setToc] = React.useState({})

  const targetActivities = useMemo(() => {
    return IS_READ_WITH_ID
      ? [matchReadWithId?.params?.id]
      : IS_MISSION
      ? mission.data?.items?.[0].Mission?.params
      : IS_ASSIGNMENT
      ? [assignment.data?.activity_id]
      : IS_PREVIEW_MISSION
      ? previewMission.data?.params ?? []
      : []
  }, [
    IS_ASSIGNMENT,
    IS_MISSION,
    IS_PREVIEW_MISSION,
    IS_READ_WITH_ID,
    assignment,
    matchReadWithId,
    mission,
    previewMission,
  ])

  const handleClickActivity = useCallback((activity) => {
    setCurrentActivity(activity)
  }, [])

  return {
    showContent: SHOW_CONTENT,
    drawer: {
      open,
      handleToggleDrawer,
    },
    readerMode: {
      IS_HTML5,
      IS_DIGIIEBOOK,
      IS_IWB,
      IS_DIGIIWB,
      IS_EPUB,
      IS_DIWB,
      IS_DIGIPUB,
      IS_ASSIGNMENT,
      IS_MISSION,
      IS_PREVIEW_MISSION,
      IS_READ,
      IS_READ_WITH_ID,
      IS_SCORM,
      IS_ASSIGNMENT_DIGIIEBOOK,
      IS_ASSIGNMENT_PREVIEW,
    },
    iRef,
    // book: pkg?.data?.version,
    epub3: {
      url: epub3URL,
    },
    html5: {
      url: html5URL,
    },
    digiiebook: {
      url: digiiebookURL,
    },
    iwb: {
      url: iwbURL,
    },
    digiiwb: {
      url: digiiwbURL,
    },
    digipub: {
      url: digiURL,
      toc,
      setToc,
      targetActivities,
    },
    scorm: {
      url: scormURL,
      folder: readPackage.data?.url,
    },
    status: {
      IS_READING,
    },
    info: {
      title: pkg.data?.title ?? '',
      packageId: packageToGet,
      baseURL,
      studentAnswers,
      currentActivity,
      currentActivityWithID,
      currentIndex,
      availableActivities,
      pages,
      currentPage,
      currentTab,
      isTabActivities,
      isTabPages,
      groupedActivities,
      tabs,
      pkg,
    },
    actions: {
      setCurrentTab,
      setCurrentPage,
      setCurrentActivity: handleClickActivity,
      setCurrentIndex,
      setAvailableActivities,
    },
  }
}

const ReaderContext = React.createContext()

export const ReaderContextProvider = ({ children }) => {
  const value = useReaderContextProvider()

  return (
    <ReaderContext.Provider value={value}>{children}</ReaderContext.Provider>
  )
}

export const useReaderContext = () => useContext(ReaderContext)
